import { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { EASE_PREMIUM, MOTION } from '@/lib/motion';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FunnelSimpleIcon,
  EnvelopeSimpleIcon,
  ArrowsCounterClockwiseIcon,
  BroadcastIcon,
} from '@phosphor-icons/react';
import { XIcon } from 'lucide-react';
import type { Offer } from '../types';

export type OfferFilterStatus = 'pending' | 'negotiating' | 'live';

const STATUS_OPTIONS: {
  id: OfferFilterStatus;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'pending',
    label: 'Pending',
    icon: <EnvelopeSimpleIcon weight="fill" className="size-4 text-amber" />,
  },
  {
    id: 'negotiating',
    label: 'Negotiating',
    icon: (
      <ArrowsCounterClockwiseIcon
        weight="fill"
        className="size-4 text-cobalt"
      />
    ),
  },
  {
    id: 'live',
    label: 'Live',
    icon: <BroadcastIcon weight="fill" className="size-4 text-emerald" />,
  },
];

export function matchesOfferFilter(
  offer: Offer,
  selectedStatuses: OfferFilterStatus[]
): boolean {
  if (selectedStatuses.length === 0) return true;
  return selectedStatuses.some(f => {
    if (f === 'pending')
      return offer.status === 'new' || offer.status === 'reviewing';
    if (f === 'negotiating')
      return (
        offer.status === 'new-counter' || offer.status === 'counter-in-review'
      );
    if (f === 'live') return offer.tab_filter === 'accepted';
    return false;
  });
}

type Props = {
  selectedStatuses: OfferFilterStatus[];
  onStatusChange: (statuses: OfferFilterStatus[]) => void;
};

export function OfferStatusFilterDropdown({
  selectedStatuses,
  onStatusChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const reduced = useReducedMotion();
  const hasFilters = selectedStatuses.length > 0;
  const tween = reduced
    ? { duration: 0 }
    : { duration: MOTION.micro, ease: EASE_PREMIUM };

  const toggleStatus = (s: OfferFilterStatus) => {
    if (selectedStatuses.includes(s)) {
      onStatusChange(selectedStatuses.filter(x => x !== s));
    } else {
      onStatusChange([...selectedStatuses, s]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <motion.button
          layout
          transition={tween}
          className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-md border border-border canvas-bg px-4 text-sm font-medium tracking-tight shadow-[var(--shadow-well)] transition-colors hover:border-border-hover ${
            open ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          <FunnelSimpleIcon className="size-4" />
          Status
          <AnimatePresence>
            {hasFilters && (
              <motion.span
                key="count"
                layout
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={tween}
                className="flex items-center justify-center size-5 rounded-full bg-card border border-border text-xs text-muted-foreground"
              >
                {selectedStatuses.length}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[220px] p-1.5 rounded-xl border border-border border-t-white/10 bg-secondary/40 shadow-elev-3 backdrop-blur-2xl"
      >
        <div className="flex flex-col gap-1.5">
          {STATUS_OPTIONS.map(option => {
            const isSelected = selectedStatuses.includes(option.id);
            return (
              <button
                key={option.id}
                onClick={() => toggleStatus(option.id)}
                className="flex items-center gap-2 h-8 p-1.5 rounded-sm text-sm font-medium tracking-tight text-foreground hover:bg-card transition-colors"
              >
                {option.icon}
                <span className="flex-1 text-left">{option.label}</span>
                <Checkbox
                  checked={isSelected}
                  tabIndex={-1}
                  className="pointer-events-none"
                />
              </button>
            );
          })}
          {hasFilters && (
            <>
              <div className="border-t border-border" />
              <button
                onClick={() => onStatusChange([])}
                className="flex items-center gap-2 px-1.5 py-4 text-sm font-medium tracking-tight text-foreground hover:bg-card transition-colors"
              >
                <span className="flex-1 text-left">Clear Filters</span>
                <XIcon className="size-5" />
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
